import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import config from '../config.js';
import { useT } from '../contexts/LocaleContext.jsx';
import { useBroadcastSurface } from '../contexts/BroadcastSurface.jsx';
import '../styles/OBSOverlay.css';

export const POLL_INTERVAL_MS = 5000;
export const MAX_BACKOFF_MS = 60000;

/**
 * The wait before the next poll after `failures` failed polls in a row (5xx
 * or a network error): the normal 5 s, doubling, capped at a minute.
 * @param {number} failures
 */
export const nextPollDelay = (failures) =>
  Math.min(MAX_BACKOFF_MS, POLL_INTERVAL_MS * Math.pow(2, Math.max(0, failures)));

/**
 * The OBS browser-source overlay (ENG-497).
 *
 * The URL carries the band's overlay token (`?overlay=`), which grants
 * read-only access to what's playing and nothing else. It used to carry the
 * session JWT (`?token=`) and poll the member queue with it, which put a
 * credential with full account access into a URL that can end up on stream.
 *
 * It reads `GET /public/overlay/{token}/current-song`, with no credentials:
 * - 200: shows the song while the band is live, and keeps polling a band
 *   that isn't live (it may go live);
 * - 400, 401 or 404: the token is wrong or was regenerated, so it stops;
 * - 5xx or a network error: backs off, 5 s up to a minute.
 * In every error state it renders nothing the audience could see. An old
 * `?token=` URL shows a small notice to regenerate the URL instead of polling.
 */
const OBSOverlay = () => {
  const [searchParams] = useSearchParams();
  const overlayToken = searchParams.get('overlay');
  const legacyUrl = !overlayToken && searchParams.has('token');
  const t = useT();
  // The overlay is itself the broadcast: no app-level overlay may show on it.
  useBroadcastSurface();

  const [currentSong, setCurrentSong] = useState(null);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    document.documentElement.classList.add('obs-overlay-active');
    return () => document.documentElement.classList.remove('obs-overlay-active');
  }, []);

  useEffect(() => {
    setCurrentSong(null);
    setIsLive(false);
    if (!overlayToken) return undefined;

    const controller = new AbortController();
    let timer = null;
    let failures = 0;
    let stopped = false;

    const schedule = (delay) => {
      if (stopped) return;
      timer = setTimeout(poll, delay);
    };

    const fail = () => {
      setIsLive(false);
      setCurrentSong(null);
    };

    const poll = async () => {
      timer = null;
      let response;
      try {
        response = await fetch(
          `${config.apiBaseUrl}/public/overlay/${encodeURIComponent(overlayToken)}/current-song`,
          { signal: controller.signal, cache: 'no-store' }
        );
      } catch (e) {
        if (e.name === 'AbortError') return;
        fail();
        failures += 1;
        schedule(nextPollDelay(failures));
        return;
      }

      if (response.status === 400 || response.status === 401 || response.status === 404) {
        stopped = true;
        fail();
        return;
      }
      if (!response.ok) {
        fail();
        failures += 1;
        schedule(nextPollDelay(failures));
        return;
      }

      try {
        const data = await response.json();
        const song = data?.now_playing;
        setIsLive(Boolean(data?.is_live));
        setCurrentSong(
          song
            ? {
                // Falls back to the localized placeholder at render time.
                title: song.title || null,
                artist: song.artist || null,
              }
            : null
        );
        failures = 0;
        schedule(POLL_INTERVAL_MS);
      } catch (e) {
        if (e.name === 'AbortError') return;
        fail();
        failures += 1;
        schedule(nextPollDelay(failures));
      }
    };

    poll();

    return () => {
      stopped = true;
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [overlayToken]);

  if (legacyUrl) {
    return (
      <div className="obs-overlay obs-overlay--notice" role="status">
        {t('obs.overlay.legacyUrl')}
      </div>
    );
  }

  if (!isLive || !currentSong) return null;

  return (
    <div className="obs-overlay" role="region" aria-label={t('obs.overlay.region')}>
      <div className="obs-overlay-now-playing" aria-live="polite">
        <div className="obs-overlay-label">{t('obs.overlay.nowPlaying')}</div>
        <div className="obs-overlay-title">{currentSong.title || t('common.fallbacks.unknownTitle')}</div>
        {currentSong.artist && (
          <div className="obs-overlay-artist">{currentSong.artist}</div>
        )}
      </div>
    </div>
  );
};

export default OBSOverlay;
